import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, CheckCircle2, User } from 'lucide-react';
import ClientSidebar from '../Sidebar/ClientSidebar';
import { useToast } from '../../hooks/use-toast';
import { parseUTC } from '../../lib/date';

const ClientHistory = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const { ordersAPI } = await import('../../api');
      const data = await ordersAPI.getOrders();
      setOrders(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger l'historique",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Terminé</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />En cours</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-[#C00000]">Historique</span> des commandes
            </h1>
            <p className="text-[#A6A6A6]">Toutes vos commandes passées et en cours</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A6A6A6]">Chargement...</p>
            </div>
          ) : orders.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="py-12 text-center">
                <p className="text-[#A6A6A6] text-lg">Aucune commande pour le moment</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.order_id} className="bg-[#1a1a1a] border-[#A6A6A6]/20 hover:border-[#C00000]/50 transition-all">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-white mb-2">
                          {order.vehicle_info.brand} {order.vehicle_info.model} {order.vehicle_info.engine}
                        </CardTitle>
                        <div className="flex flex-wrap gap-3 text-sm text-[#A6A6A6]">
                          <span>Commande {order.order_id}</span>
                          <span>•</span>
                          <span>{order.service}</span>
                          <span>•</span>
                          <span>{parseUTC(order.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1">Prix</p>
                        <p className="text-white font-semibold">{order.price}€ HT</p>
                      </div>
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1">Statut paiement</p>
                        <p className="text-white font-semibold capitalize">{order.payment_status}</p>
                      </div>
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1">Fichier original</p>
                        <p className="text-white font-semibold text-sm">
                          {order.original_file_name ? '✓ Uploadé' : '✗ En attente'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1 flex items-center gap-1">
                          <User className="h-3 w-3" />
                          Ingénieur assigné
                        </p>
                        <p className="text-[#C00000] font-semibold">
                          {order.engineer_name || 'En attente d\'assignation'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientHistory;
